/** /import_runs/ — ops-wide XML import run search across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to
 * /event_delivery_logs/, but over Import_Run (see ImportRunFilterNode in the
 * OpenAPI spec). Rows have UUID (string) ids.
 *
 * Grouping: the default list returns TOP-LEVEL rows only (standalone runs +
 * group parents with a `child_count`); pass `parent` to fetch that group's
 * children instead. Record-reference search namespaces hit the
 * Import_Run_Index table with EXACT value matches — child index rows are
 * mirrored onto the parent, so matching a child surfaces its parent row.
 */
import { apiGet, apiPost, client, extendedListConfig } from './client';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface ImportRunSourceBrief {
  code: string;
  name: string;
  state: number;
}

export interface ImportRunListItem {
  /** UUID. */
  id: string;
  /** Group id — null for standalone runs; equals `id` on a group parent. */
  group: string | null;
  is_parent: boolean;
  /** Number of child runs (top-level list mode only; 0 elsewhere). */
  child_count: number;
  created_ts: string;
  processing_started_ts: string | null;
  processing_finished_ts: string | null;
  /** Seconds, when both processing timestamps are set. */
  processing_duration: number | null;
  /** IMPORT_RUN_STATES — see IMPORT_STATUS_LABELS (1-based). */
  state: number;
  /** Import_Run.ORIGINS — see IMPORT_ORIGIN_LABELS (0-based). */
  origin: number;
  method: number | null;
  request_id: string | null;
  task_id: string | null;
  url: string | null;
  /** Owning tenant; null for system-level runs. `environment`: the Tenant cell marks a sandbox. */
  top: { id: number; name: string; environment?: TourOperatorEnvironment | null } | null;
  sources: ImportRunSourceBrief[];
}

export interface ImportRunSourceRun {
  id: number;
  code: string;
  name: string;
  state: number;
  /** 1 Strict, 2 Relaxed. */
  policy: number | null;
  /** 1 Full, 2 Append. */
  mode: number | null;
  processing_report: string | null;
  stats: Record<string, unknown> | null;
}

export interface ImportRunDetail extends Omit<ImportRunListItem, 'sources'> {
  input_options: Record<string, unknown> | null;
  processing_stats: Record<string, unknown> | null;
  sources: ImportRunSourceRun[];
  /** Runs created by reimporting this one. */
  reimport: { id: string; created_ts: string | null }[];
  /** Child-run summaries (capped at 200). */
  children: ImportRunListItem[];
}

/** One normalized log line (structured S3 log or legacy text parse). */
export interface ImportRunLogRow {
  id: number;
  date: string | null;
  time: string | null;
  ts: string | null;
  host: string | null;
  lineno: string | null;
  level: string | null;
  message: string | null;
}

export type ImportRunListResponse = PaginatedResponse<ImportRunListItem> & {
  count_is_capped: boolean;
};

export interface ImportRunListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  /** Children mode: list this group parent's child runs. */
  parent?: string;
}

function toQuery(p: ImportRunListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  if (p.parent) usp.set('parent', p.parent);
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(p: ImportRunListParams = {}): Promise<ImportRunListResponse> {
  const qs = toQuery(p);
  return apiGet(`/import_runs/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** GET /import_runs/{id}/ — full run + per-source rows + reimport links + children. */
export async function retrieve(id: string): Promise<ImportRunDetail> {
  return apiGet(`/import_runs/${id}/`);
}

/** GET /import_runs/{id}/input_data/ — the stored input document as text. */
export async function inputData(
  id: string,
): Promise<{ input_data: string | null; format: 'xml' | 'json' }> {
  return apiGet(`/import_runs/${id}/input_data/`);
}

/** GET /import_runs/{id}/logs/ — the whole normalized log in one response. */
export async function logs(id: string): Promise<{ logs: ImportRunLogRow[] }> {
  return apiGet(`/import_runs/${id}/logs/`);
}

/** POST /import_runs/{id}/resubmit/ — reimport, optionally with edited XML. */
export async function resubmit(
  id: string,
  body: { send_notifications: boolean; data?: string | null },
): Promise<{ id: string }> {
  return apiPost(`/import_runs/${id}/resubmit/`, body);
}

/** POST /import_runs/upload/ — multipart upload of an XML/JSON import file
 *  for a chosen tenant. */
export async function upload(args: {
  file: File;
  tourOperatorId: number;
}): Promise<{ id: string; converted: boolean }> {
  const fd = new FormData();
  fd.append('file', args.file);
  fd.append('tour_operator_id', String(args.tourOperatorId));
  // Let axios set the multipart boundary itself.
  const { data } = await client.post<{ id: string; converted: boolean }>(
    '/import_runs/upload/',
    fd,
    { headers: { 'Content-Type': undefined } },
  );
  return data;
}

// ---- AI diagnostics (perm: console_import_ai) -------------------------------
// One tool serves import runs AND api requests; types live in features/ai-diag.

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/import_runs/diagnose/launch_context/');
}

/** POST /import_runs/{id}/diagnose/ — start an AI diagnostic run (or a
 *  follow-up when `thread_task_id` is set). 202 { task_id } — poll bgtasks;
 *  409 carries the already-running task_id (adopt it). */
export async function startDiagnose(
  id: string,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/import_runs/${id}/diagnose/`, body);
}

// Tenant suggestions are the SAME shared Bookings autocomplete endpoint;
// re-export the fetcher factory so importRunNamespaces has one import.
export { makeSuggestionFetcher } from './bookings';
